import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { Link } from 'react-router-dom';

const { FiMapPin, FiUsers, FiStar, FiTrendingUp, FiEye, FiCalendar, FiDollarSign, FiFilter, FiGrid, FiList } = FiIcons;

const PortfolioSection = () => {
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [viewMode, setViewMode] = useState('grid');

  const properties = [
    {
      id: 1,
      title: "Penthouse Vista Duomo",
      location: "Centro Storico, Milano",
      type: "Attico di Lusso",
      category: "luxury",
      guests: 6,
      rating: 4.9,
      reviews: 142,
      revenue: "+72%",
      monthlyRevenue: "€6.800",
      previousRevenue: "€3.900",
      occupancy: "94%",
      image: "https://media-manager-c.questera.ai/greta-media/32dc413054558dbd6d4d3c4a3e2032d0a5a88f3a3f9e6c5dd10a15135c65ae90de62d22e50b183c419625584e3716ff8/images/aW1hZ2UvanBlZw==/d31c996d43abab438db6a2eaffc18e56.jpg",
      features: ["Terrazza panoramica", "Domotica avanzata", "Interior design firmato", "Concierge 24/7"],
      description: "Esclusivo attico nel cuore pulsante di Milano, ottimizzato per un pubblico internazionale high-spending.",
      managementPeriod: "24 mesi",
      achievements: ["Top Yield Milano", "Superhost Platinum", "Award Design 2024"]
    },
    {
      id: 2,
      title: "Design Loft Porta Nuova",
      location: "Gae Aulenti, Milano",
      type: "Loft Corporate",
      category: "business",
      guests: 4,
      rating: 4.8,
      reviews: 115,
      revenue: "+58%",
      monthlyRevenue: "€4.100",
      previousRevenue: "€2.600",
      occupancy: "88%",
      image: "https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1753288194892-Loft-in-vendita-milano-608858958.jpg",
      features: ["Smart working ready", "Vicino Metro Garibaldi", "Arredi di design", "Check-in automatico"],
      description: "Loft strategico per viaggiatori d'affari e professionisti nel distretto finanziario di Milano.",
      managementPeriod: "14 mesi",
      achievements: ["Business Choice", "Efficiency Prize", "Guest Favorite"]
    },
    {
      id: 3,
      title: "Residenza Bosco Verticale",
      location: "Porta Nuova, Milano",
      type: "Residenza Esclusiva",
      category: "luxury",
      guests: 4,
      rating: 4.9,
      reviews: 89,
      revenue: "+65%",
      monthlyRevenue: "€7.200",
      previousRevenue: "€4.300",
      occupancy: "91%",
      image: "https://media-manager-c.questera.ai/greta-media/32dc413054558dbd6d4d3c4a3e2032d0a5a88f3a3f9e6c5dd10a15135c65ae90de62d22e50b183c419625584e3716ff8/images/aW1hZ2UvanBlZw==/039aeb01ebaeb4b3127d00de61ddb802.jpg",
      features: ["Vista panoramica", "Design contemporaneo", "Servizi building 5 stelle", "Terrazzo piantumato"],
      description: "Appartamento di prestigio nel pluripremiato Bosco Verticale, con vista mozzafiato sullo skyline milanese.",
      managementPeriod: "18 mesi",
      achievements: ["Eco-Luxury Premium", "Top Location", "Guest Favorite"]
    }
  ];

  const filters = [
    { key: 'all', label: 'Tutti gli Immobili', count: properties.length },
    { key: 'luxury', label: 'Luxury & Penthouse', count: properties.filter(p => p.category === 'luxury').length },
    { key: 'business', label: 'Corporate & Business', count: properties.filter(p => p.category === 'business').length }
  ];

  const filteredProperties = selectedFilter === 'all' 
    ? properties 
    : properties.filter(property => property.category === selectedFilter);

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="mb-6"
          >
            <span className="bg-brand-blue text-white px-4 py-2 rounded-full text-sm font-black uppercase tracking-widest border border-brand-gold">
              Portfolio Milano & Dintorni
            </span>
          </motion.div>
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
            className="text-3xl md:text-5xl font-bold text-brand-blue mb-6 uppercase tracking-tight"
          >
            Asset Strategici in Gestione
          </motion.h2>
          <p className="text-xl text-brand-subtle max-w-3xl mx-auto leading-relaxed">
            Gestiamo con successo immobili nelle zone più richieste di Milano e località satellite ad alto potenziale.
          </p>
        </div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          className="flex flex-col lg:flex-row justify-between items-center mb-12 gap-6"
        >
          <div className="flex flex-wrap gap-3">
            {filters.map((filter) => (
              <button
                key={filter.key}
                onClick={() => setSelectedFilter(filter.key)}
                className={`px-6 py-3 rounded-xl font-bold transition-all flex items-center space-x-2 border-2 ${
                  selectedFilter === filter.key
                    ? 'bg-brand-blue text-white border-brand-blue shadow-lg'
                    : 'bg-white text-brand-blue border-brand-silver/30 hover:border-brand-gold'
                }`}
              >
                <span>{filter.label}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  selectedFilter === filter.key ? 'bg-brand-gold text-white' : 'bg-brand-silver/20'
                }`}>
                  {filter.count}
                </span>
              </button>
            ))}
          </div>

          <div className="flex items-center space-x-3 bg-brand-blue/5 p-2 rounded-2xl border border-brand-silver/20">
            <button 
              onClick={() => setViewMode('grid')}
              className={`p-3 rounded-xl transition-all ${viewMode === 'grid' ? 'bg-white shadow-md text-brand-gold' : 'text-brand-blue/50'}`}
            ><SafeIcon icon={FiGrid} className="h-5 w-5" /></button>
            <button 
              onClick={() => setViewMode('list')}
              className={`p-3 rounded-xl transition-all ${viewMode === 'list' ? 'bg-white shadow-md text-brand-gold' : 'text-brand-blue/50'}`}
            ><SafeIcon icon={FiList} className="h-5 w-5" /></button>
          </div>
        </motion.div>

        <div className={`grid gap-10 ${viewMode === 'grid' ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'}`}>
          {filteredProperties.map((property, index) => (
            <motion.div
              key={property.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              viewport={{ once: true }}
              className={`bg-white rounded-[2rem] shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-500 group border border-brand-silver/10 ${viewMode === 'list' ? 'flex flex-col lg:flex-row' : ''}`}
            >
              <div className={`relative overflow-hidden ${viewMode === 'list' ? 'lg:w-1/2' : ''}`}>
                <img 
                  src={property.image} 
                  alt={property.title} 
                  className={`w-full object-cover group-hover:scale-110 transition-transform duration-700 ${viewMode === 'list' ? 'h-80 lg:h-full' : 'h-72'}`}
                />
                <div className="absolute top-6 left-6 bg-brand-blue/80 backdrop-blur-md text-white px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest border border-brand-gold/30">
                  {property.type}
                </div>
                <div className="absolute bottom-6 left-6 bg-brand-gold text-white px-4 py-2 rounded-xl text-sm font-bold flex items-center shadow-lg">
                  <SafeIcon icon={FiTrendingUp} className="h-4 w-4 mr-2" />
                  {property.revenue} ROI
                </div>
              </div>
              
              <div className={`p-8 ${viewMode === 'list' ? 'lg:w-1/2' : ''}`}>
                <div className="flex items-center text-brand-gold mb-3">
                  <SafeIcon icon={FiMapPin} className="h-4 w-4 mr-2" />
                  <span className="text-xs font-black uppercase tracking-[0.2em]">{property.location}</span>
                </div>
                
                <h3 className="text-2xl font-bold text-brand-blue mb-4 group-hover:text-brand-gold transition-colors">{property.title}</h3>
                
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-brand-blue/5 p-4 rounded-2xl border border-brand-silver/10">
                    <div className="text-xl font-black text-brand-blue">{property.monthlyRevenue}</div>
                    <div className="text-[10px] uppercase font-bold text-brand-subtle tracking-tighter">Ricavo Mensile</div>
                  </div>
                  <div className="bg-brand-blue/5 p-4 rounded-2xl border border-brand-silver/10">
                    <div className="text-xl font-black text-brand-blue">{property.occupancy}</div>
                    <div className="text-[10px] uppercase font-bold text-brand-subtle tracking-tighter">Occupazione</div>
                  </div>
                </div>

                <div className="flex items-center justify-between text-brand-subtle text-sm mb-6 border-b border-brand-silver/10 pb-4">
                  <div className="flex items-center space-x-4">
                    <span className="flex items-center"><SafeIcon icon={FiUsers} className="mr-2 h-4 w-4" /> {property.guests}</span>
                    <span className="flex items-center"><SafeIcon icon={FiStar} className="mr-1 h-4 w-4 text-brand-gold" /> {property.rating}</span>
                  </div>
                  <span className="font-bold text-brand-blue">{property.managementPeriod}</span>
                </div>

                <div className="flex flex-wrap gap-2">
                  {property.achievements.map((a, i) => (
                    <span key={i} className="bg-brand-silver/20 text-brand-blue px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest">{a}</span>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PortfolioSection;